/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 * Changes improve clarity, reduce technical jargon, and enhance UX.
 *
 * File Size Check:
 * - Current: 458 lines
 * - Adding: ~8 lines (header + inline comments)
 * - Projected: 466 lines
 * - Decision: Proceed (message changes only)
 *
 * Typography Compliance (UI files only):
 * - N/A (notification messages only)
 *
 * Related Files:
 * - utils/notifications.ts
 * - All auth-related components
 */

import { FunctionComponent, useState } from "react";
import { useFormik } from "formik";
import { NavigateFunction } from "react-router-dom";
import { addUser } from "../../services/usersService";
import { signupValidationSchema } from "./validationSchemas";
import PasswordInput from "./PasswordInput";
import GoogleAuthButton from "./GoogleAuthButton";
import GoogleSignupTosModal from "./GoogleSignupTosModal";
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    UI_STANDARDIZATION
 * Date:         2025-01-XX
 * Modified By:  Cursor
 *
 * Description:
 * Replaced direct toast.* calls with centralized notify helper for consistent messaging.
 *
 * Related Screens / Modules:
 * - client/src/components/auth/SignupForm.tsx
 * - client/src/utils/notifications.ts
 */

import { notify } from "../../utils/notifications";
// [2025-12-20] Tracing: Import trace components
import { TraceStatus } from "../traces/TraceStatus";

interface SignupFormProps {
  navigate: NavigateFunction;
  onAuthSuccess?: () => void;
  onClose: () => void;
  onGoogleSignup: (
    tosAccepted: boolean,
    marketingOptIn: boolean,
    formData?: {
      firstName?: string;
      lastName?: string;
      phone?: string;
      address?: string;
    },
  ) => void;
  onSwitchToLogin?: () => void;
}

interface SignupFormValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;
  address: string;
  tosAccepted: boolean;
  marketingOptIn: boolean;
}

const SignupForm: FunctionComponent<SignupFormProps> = ({
  navigate,
  onAuthSuccess,
  onClose,
  onGoogleSignup,
  onSwitchToLogin,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  // [2025-12-20] Tracing: Track email trace ID
  const [emailTraceId, setEmailTraceId] = useState<string | null>(null);
  // [2025-01-XX] Google signup T&C: State for T&C modal visibility
  const [showTosModal, setShowTosModal] = useState(false);

  const formik = useFormik<SignupFormValues>({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      phone: "",
      address: "",
      tosAccepted: false,
      marketingOptIn: false,
    },
    validationSchema: signupValidationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        // Remove confirmPassword from the values before sending to server
        const { confirmPassword, ...userValues } = values;
        // Explicitly set isAdmin to false for new users
        // Include tosAccepted and marketingOptIn in the payload
        const userData = { ...userValues, isAdmin: false };
        const response = await addUser(userData);

        // [2025-12-20] Tracing: Capture emailTraceId from signup response
        if (response.data?.emailTraceId) {
          setEmailTraceId(response.data.emailTraceId);
        }

        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.success(
          "Check your email to verify your account and start using UAVZone",
        );
        // Don't auto-login - user must verify email first
        // Switch to login tab so user can login after verification
        if (onSwitchToLogin) {
          onSwitchToLogin();
        }
        // Clear form
        formik.resetForm();
      } catch (err: any) {
        console.error("Signup error:", err);

        // Handle network errors
        if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
          // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
          notify.error(err, "We can't connect right now. Please check your internet connection and try again");
          return;
        }

        // Parse error response from server
        let errorMessage = "We couldn't create your account. Please try again or contact support if the problem continues";

        if (err.response?.data) {
          const errorData = err.response.data;

          // Handle error format: { errors: [{ path: string, message: string }] }
          if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
            // Get all error messages
            const messages = errorData.errors.map((e: any) => e.message).filter(Boolean);
            if (messages.length > 0) {
              errorMessage = messages.join(". ");
            }
          }
          // Handle simple string error
          else if (typeof errorData === "string") {
            errorMessage = errorData;
          }
          // Handle object with message or error field
          else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage =
              typeof errorData.error === "string"
                ? errorData.error
                : errorData.error.message || errorMessage;
          }
        }

        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.error(err, errorMessage);

        // Set field-specific errors in formik if available
        if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
          const fieldErrors: Record<string, string> = {};
          err.response.data.errors.forEach((error: any) => {
            if (error.path && error.path !== "general" && error.message) {
              fieldErrors[error.path] = error.message;
            }
          });

          // Set errors in formik for field-specific errors
          if (Object.keys(fieldErrors).length > 0) {
            Object.keys(fieldErrors).forEach((field) => {
              formik.setFieldError(field, fieldErrors[field]);
            });
          }
        }
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Compute canSubmit flag - button is enabled only when all required fields are valid
  // Required fields: firstName, lastName, email, password, tosAccepted
  // Location: client/src/components/auth/SignupForm.tsx
  const canSubmit =
    formik.isValid &&
    formik.dirty &&
    !!formik.values.firstName?.trim() &&
    !!formik.values.lastName?.trim() &&
    !!formik.values.email?.trim() &&
    !!formik.values.password &&
    !!formik.values.tosAccepted;

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    // Mark all fields as touched to show errors when user tries to submit
    const touchedFields: Record<string, boolean> = {};
    Object.keys(formik.values).forEach((key) => {
      touchedFields[key] = true;
    });
    formik.setTouched(touchedFields);
    // Let formik handle the submission - it will validate and only call onSubmit if valid
    formik.handleSubmit(e);
  };

  return (
    <form onSubmit={handleFormSubmit}>
      {/* [2026-02-07] style-extraction: moved inline styles to _auth.scss */}
      {/* First Name and Last Name - Side by Side */}
      <div className="mb-4 auth-field-container">
        <div className="d-flex gap-3 auth-name-row">
          {/* First Name */}
          <div className="auth-name-field">
            <input
              type="text"
              className="form-control email-input auth-text-18 auth-name-input"
              placeholder="First Name*"
              name="firstName"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.firstName && formik.errors.firstName && (
              <div className="text-danger mt-1 text-center auth-error-message">
                {formik.errors.firstName}
              </div>
            )}
          </div>

          {/* Last Name */}
          <div className="auth-name-field">
            <input
              type="text"
              className="form-control email-input auth-text-18 auth-name-input"
              placeholder="Last Name*"
              name="lastName"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.lastName && formik.errors.lastName && (
              <div className="text-danger mt-1 text-center auth-error-message">
                {formik.errors.lastName}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Email */}
      <div className="mb-4 auth-field-container">
        <input
          type="email"
          className="form-control email-input auth-text-18"
          placeholder="Email*"
          name="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
        {formik.touched.email && formik.errors.email && (
          <div className="text-danger mt-1 text-center auth-error-message">
            {formik.errors.email}
          </div>
        )}
      </div>

      {/* Password */}
      <PasswordInput
        name="password"
        placeholder="Password*"
        value={formik.values.password}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        showPassword={showPassword}
        onTogglePassword={() => setShowPassword(!showPassword)}
        error={formik.errors.password}
        touched={formik.touched.password}
      />

      {/* Confirm Password */}
      <PasswordInput
        name="confirmPassword"
        placeholder="Confirm Password*"
        value={formik.values.confirmPassword}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        showPassword={showConfirmPassword}
        onTogglePassword={() => setShowConfirmPassword(!showConfirmPassword)}
        error={formik.errors.confirmPassword}
        touched={formik.touched.confirmPassword}
      />

      {/* Phone */}
      <div className="mb-4 auth-field-container">
        <input
          type="tel"
          className="form-control email-input auth-text-18"
          placeholder="Phone"
          name="phone"
          value={formik.values.phone}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
        {formik.touched.phone && formik.errors.phone && (
          <div className="text-danger mt-1 text-center auth-error-message">
            {formik.errors.phone}
          </div>
        )}
      </div>

      {/* Address */}
      <div className="mb-4 auth-field-container">
        <input
          type="text"
          className="form-control email-input auth-text-18"
          placeholder="Address"
          name="address"
          value={formik.values.address}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
        {formik.touched.address && formik.errors.address && (
          <div className="text-danger mt-1 text-center auth-error-message">
            {formik.errors.address}
          </div>
        )}
      </div>

      {/* Terms of Service and Privacy Policy */}
      <div className="mb-4 auth-field-container">
        <div className="d-flex align-items-start auth-checkbox-row">
          <input
            type="checkbox"
            id="tosAccepted"
            name="tosAccepted"
            checked={formik.values.tosAccepted}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="me-2 mt-1 auth-checkbox-input"
          />
          <label
            htmlFor="tosAccepted"
            className="u-P1-18 auth-checkbox-label"
          >
            I agree to the{" "}
            <a href="https://uavtradezone.com/terms-of-use/" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>{" "}
            and{" "}
            <a href="https://uavtradezone.com/privacy-policy/" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
            *
          </label>
        </div>
        {formik.touched.tosAccepted && formik.errors.tosAccepted && (
          <div className="text-danger mt-1 text-center auth-checkbox-row">
            {formik.errors.tosAccepted}
          </div>
        )}
      </div>

      {/* Marketing Opt-in */}
      <div className="mb-4 auth-field-container">
        <div className="d-flex align-items-start auth-checkbox-row">
          <input
            type="checkbox"
            id="marketingOptIn"
            name="marketingOptIn"
            checked={formik.values.marketingOptIn}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="me-2 mt-1 auth-checkbox-input"
          />
          <label
            htmlFor="marketingOptIn"
            className="u-P1-18 auth-checkbox-label"
          >
            I would like to receive marketing communications and product updates
          </label>
        </div>
      </div>

      {/* Google row */}
      <div className="auth-field-container">
        <GoogleAuthButton
          onClick={() => {
            // [2025-01-XX] Google signup T&C: Skip modal if terms already accepted
            if (formik.values.tosAccepted) {
              // Terms already accepted, proceed directly with form values
              onGoogleSignup(
                formik.values.tosAccepted,
                formik.values.marketingOptIn,
                {
                  firstName: formik.values.firstName || undefined,
                  lastName: formik.values.lastName || undefined,
                  phone: formik.values.phone || undefined,
                  address: formik.values.address || undefined,
                },
              );
            } else {
              // Terms not accepted, show modal
              setShowTosModal(true);
            }
          }}
          label="Sign up with Google"
          ariaLabel="Sign up with Google"
        />
      </div>

      {/* Google Signup T&C Modal */}
      <GoogleSignupTosModal
        show={showTosModal}
        onConfirm={(tosAccepted, marketingOptIn) => {
          setShowTosModal(false);
          // When modal is used, also pass form values if available
          onGoogleSignup(tosAccepted, marketingOptIn, {
            firstName: formik.values.firstName || undefined,
            lastName: formik.values.lastName || undefined,
            phone: formik.values.phone || undefined,
            address: formik.values.address || undefined,
          });
        }}
        onCancel={() => setShowTosModal(false)}
      />

      {/* Primary CTA */}
      {/* Button found at: client/src/components/auth/SignupForm.tsx line ~342 */}
      {/* [2026-02-11] cookie-auth: Added mt-3 spacing between Google and Create Account buttons */}
      <div className="auth-field-container mt-3">
        <button className="btn-black" type="submit" disabled={!canSubmit || isSubmitting}>
          <span className="signin-button-text">Create Account</span>
        </button>
      </div>

      {/* [2025-12-20] Tracing: Show email verification status */}
      {emailTraceId && (
        <div className="auth-field-container mt-3">
          <TraceStatus
            traceId={emailTraceId}
            showDetails={false}
            onComplete={(trace) => {
              // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
              notify.success("We've sent you a verification email. Please check your inbox");
              setEmailTraceId(null); // Clear after success
            }}
            onFailed={(trace) => {
              // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
              notify.error(
                trace?.error ?? null,
                "We ran into a problem sending the email. Please try again",
              );
              setEmailTraceId(null); // Clear after failure
            }}
          />
        </div>
      )}
    </form>
  );
};

export default SignupForm;
